import { useState, useEffect, useContext } from "react";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Search,
  Calendar,
  Loader2,
} from "lucide-react";
import { exportToExcel } from "@/utils/excel-export";
import { format } from "date-fns";
import { SaleReport, SelectData, Report } from "@/types/api";
import { categoryService, productService, reportService } from "@/services/api";
import { useToast } from "@/hooks/use-toast";

const SaleReportPage = () => {
  const { toast } = useToast();
  const [reports, setReports] = useState<SaleReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<SaleReport[]>([]);
  const [categories, setCategories] = useState<SelectData[]>([]);
  const [products, setProducts] = useState<SelectData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [isLoading, setIsLoading] = useState(true);

  // Fetch categories on component mount 
  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const categories = await categoryService.getForSelect();
        setCategories(categories);
      } catch (error) {
        toast({
          variant: "destructive",
          title: "Fetch Categories",
          description: error instanceof Error ? error.message : error
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Handle search
  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const categoryId = 0;
      const productId = 0;
      const newSearch: Report = {
        categoryId,
        productId,
        startDate,
        endDate,
      };
      const reportsData = await reportService.getSales(newSearch);
      if (reportsData.length === 0)
        toast({
          title: "Search Report",
          description: "No reports found matching your filters"
        });
      else {
        setReports(reportsData);
        setFilteredReports(reportsData);
      }

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Search Report",
        description: error instanceof Error ? error.message : error
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter reports based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredReports(reports);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = reports.filter(report =>
        report.product.toLowerCase().includes(query) ||
        report.description.toLowerCase().includes(query) ||
        report.category.toLowerCase().includes(query) ||
        report.type.toLowerCase().includes(query) ||
        (report.notes && report.notes.toLowerCase().includes(query))
      );
      setFilteredReports(filtered);
    }
  }, [searchQuery, reports]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCategoryChange = async (category: string) => {
    setSelectedCategory(category);

    const products = await productService.getForSelect(1);
    setProducts(products);
  };

  const handleProductChange = (product: string) => {
    setSelectedProduct(product);
  };

  const handleExportToExcel = () => {
    // Format the data for Excel export
    const exportData = filteredReports.map(report => ({
      Name: report.name,
      Category: report.category,
      Product: report.product,
      Description: report.description,
      Type: report.type,
      UnitPrice: report.unitPrice,
      Tax: report.tax,
      Quantity: report.quantity,
      Discount: report.discount,
      Total: report.total,
      PaymentMode: report.paymentMode,
      InvoiceNumber: report.invoiceNumber,
      Notes: report.notes,
      "Sold At": format(new Date(report.soldAt), "yyyy-MM-dd HH:mm")
    }));

    // Generate filename with current date
    const filename = `Sale_Report_${format(new Date(), "yyyy-MM-dd")}`;

    // Export to Excel
    exportToExcel(exportData, filename);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy");
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Sale Report</h1>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium">Start Date</label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="date"
                    className="pl-8"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">End Date</label>
                <div className="relative">
                  <Calendar className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="date"
                    className="pl-8"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(category => (
                      <SelectItem key={category.id} value={category.id.toString()}>{category.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Product</label>
                <Select value={selectedProduct} onValueChange={handleProductChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    {products.map(product => (
                      <SelectItem key={product.id} value={product.id.toString()}>{product.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Search</label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    placeholder="Search reports..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Button onClick={handleSearch}>
                  <Search size={16} className="mr-2" />
                  Search Sale
                </Button>
              </div>
              <div className="space-y-2">
                <Button onClick={handleExportToExcel} disabled={filteredReports.length === 0}>
                  <Download size={16} className="mr-2" />
                  Export to Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reports Table */}
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Unit Price</TableHead>
                  <TableHead>Tax (%)</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Discount (%)</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>InvoiceNumber</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Sold At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-10">
                      <div className="flex justify-center">
                        <Loader2 size={30} className="animate-spin text-primary" />
                      </div>
                      <p className="mt-2 text-muted-foreground">Loading reports...</p>
                    </TableCell>
                  </TableRow>
                ) : filteredReports.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">
                      {searchQuery || startDate || endDate || selectedCategory !== "all" || selectedType !== "all"
                        ? "No reports found matching your filters"
                        : "No sale reports available"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">{report.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{report.category}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{report.product}</TableCell>
                      <TableCell className="max-w-xs truncate">{report.description}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            report.type === 'Retail'
                              ? "default"
                              : report.type === 'Wholesale'
                                ? "secondary"
                                : "outline"
                          }
                        >
                          {report.type}
                        </Badge>
                      </TableCell>
                      <TableCell>{report.unitPrice.toFixed(2)}</TableCell>
                      <TableCell>{report.tax}</TableCell>
                      <TableCell>{report.quantity}</TableCell>
                      <TableCell>{report.discount}</TableCell>
                      <TableCell>{report.total}</TableCell>
                      <TableCell>{report.invoiceNumber}</TableCell>
                      <TableCell className="max-w-xs truncate">{report.notes || "-"}</TableCell>
                      <TableCell>{formatDate(report.soldAt)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SaleReportPage;